/**
 * Appointment Extractor
 * 
 * Responsible for extracting appointment data from Office.js appointment items.
 * Follows Single Responsibility Principle.
 */

/* global Office */

import { Logger } from '../utils/logger.js';
import { DateUtils } from '../utils/date-utils.js';
import { EmailExtractor } from './email-extractor.js';

/**
 * Extracts body content from appointment
 */
class AppointmentBodyExtractor {
  static async extract(appointmentItem) {
    return new Promise((resolve) => {
      appointmentItem.body.getAsync(Office.CoercionType.Text, (result) => {
        resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : '');
      });
    });
  }
}

/**
 * Extracts subject from appointment (handles both sync and async)
 */
class AppointmentSubjectExtractor {
  static async extract(appointmentItem) {
    return new Promise((resolve) => {
      if (typeof appointmentItem.subject === 'string') {
        resolve(appointmentItem.subject);
      } else if (appointmentItem.subject && typeof appointmentItem.subject.getAsync === 'function') {
        appointmentItem.subject.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : '');
        });
      } else {
        resolve(appointmentItem.subject || '');
      }
    });
  }
}

/**
 * Extracts location from appointment (handles both sync and async)
 */
class AppointmentLocationExtractor {
  static async extract(appointmentItem) {
    return new Promise((resolve) => {
      if (typeof appointmentItem.location === 'string') {
        resolve(appointmentItem.location);
      } else if (appointmentItem.location && typeof appointmentItem.location.getAsync === 'function') {
        appointmentItem.location.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : '');
        });
      } else {
        resolve(appointmentItem.location || '');
      }
    });
  }
}

/**
 * Extracts start time from appointment (handles both sync and async)
 */
class AppointmentStartTimeExtractor {
  static async extract(appointmentItem) {
    return new Promise((resolve) => {
      if (appointmentItem.start && typeof appointmentItem.start.getAsync === 'function') {
        appointmentItem.start.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : new Date());
        });
      } else {
        resolve(appointmentItem.start || new Date());
      }
    });
  }
}

/**
 * Extracts end time from appointment (handles both sync and async)
 */
class AppointmentEndTimeExtractor {
  static async extract(appointmentItem) {
    return new Promise((resolve) => {
      if (appointmentItem.end && typeof appointmentItem.end.getAsync === 'function') {
        appointmentItem.end.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : new Date());
        });
      } else {
        resolve(appointmentItem.end || new Date());
      }
    });
  }
}

/**
 * Extracts organizer from appointment (handles both sync and async)
 */
class AppointmentOrganizerExtractor {
  static async extract(appointmentItem) {
    return new Promise((resolve) => {
      if (appointmentItem.organizer && typeof appointmentItem.organizer.getAsync === 'function') {
        appointmentItem.organizer.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : null);
        });
      } else {
        resolve(appointmentItem.organizer || null);
      }
    });
  }
}

/**
 * Extracts attendee emails from appointment
 */
class AppointmentAttendeeExtractor {
  static async extractEmails(appointmentItem) {
    const [requiredAttendeesResult, optionalAttendeesResult] = await Promise.allSettled([
      this._extractRequiredAttendees(appointmentItem),
      this._extractOptionalAttendees(appointmentItem)
    ]);

    const requiredAttendees = requiredAttendeesResult.status === 'fulfilled' ? requiredAttendeesResult.value : [];
    const optionalAttendees = optionalAttendeesResult.status === 'fulfilled' ? optionalAttendeesResult.value : [];

    // Extract emails with fallback property names
    const requiredEmails = this._extractEmailsFromAttendeeList(requiredAttendees);
    const optionalEmails = this._extractEmailsFromAttendeeList(optionalAttendees);
    
    return {
      required: requiredEmails,
      optional: optionalEmails
    };
  }

  static async _extractRequiredAttendees(appointmentItem) {
    return new Promise((resolve) => {
      if (appointmentItem.requiredAttendees && typeof appointmentItem.requiredAttendees.getAsync === 'function') {
        appointmentItem.requiredAttendees.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : []);
        });
      } else {
        resolve(Array.isArray(appointmentItem.requiredAttendees) ? appointmentItem.requiredAttendees : []);
      }
    });
  }

  static async _extractOptionalAttendees(appointmentItem) {
    return new Promise((resolve) => {
      if (appointmentItem.optionalAttendees && typeof appointmentItem.optionalAttendees.getAsync === 'function') {
        appointmentItem.optionalAttendees.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : []);
        });
      } else {
        resolve(Array.isArray(appointmentItem.optionalAttendees) ? appointmentItem.optionalAttendees : []);
      }
    });
  }

  static _extractEmailsFromAttendeeList(attendees) {
    return attendees
      .map(attendee => {
        const email = attendee?.emailAddress || attendee?.address || attendee?.email || 
                     (typeof attendee === 'string' ? attendee : null);
        return typeof email === 'string' ? email.toLowerCase() : null;
      })
      .filter(Boolean);
  }
}

/**
 * Main Appointment Extractor Service
 * Follows Single Responsibility Principle and Dependency Inversion Principle
 */
export class AppointmentExtractor {
  /**
   * Extract complete appointment data from Office.js appointment item
   */
  static async extractAppointmentData(appointmentItem) {
    Logger.debug('Getting appointment properties asynchronously...');
    
    // Get all appointment properties asynchronously
    const [bodyResult, subjectResult, locationResult, startResult, endResult, organizerResult] = await Promise.allSettled([
      AppointmentBodyExtractor.extract(appointmentItem),
      AppointmentSubjectExtractor.extract(appointmentItem),
      AppointmentLocationExtractor.extract(appointmentItem),
      AppointmentStartTimeExtractor.extract(appointmentItem),
      AppointmentEndTimeExtractor.extract(appointmentItem),
      AppointmentOrganizerExtractor.extract(appointmentItem)
    ]);

    const body = bodyResult.status === 'fulfilled' ? bodyResult.value : '';
    const subject = subjectResult.status === 'fulfilled' ? subjectResult.value : '';
    const location = locationResult.status === 'fulfilled' ? locationResult.value : '';
    const startTime = startResult.status === 'fulfilled' ? startResult.value : new Date();
    const endTime = endResult.status === 'fulfilled' ? endResult.value : new Date();
    const organizer = organizerResult.status === 'fulfilled' ? organizerResult.value : null;

    // Get attendee emails asynchronously
    const attendeeEmails = await AppointmentAttendeeExtractor.extractEmails(appointmentItem);

    // Get unique appointment identifier
    const uniqueAppointmentId = this.getUniqueAppointmentId(appointmentItem);

    return {
      type: 'appointment',
      subject: subject || '',
      body: body || '',
      location: location || '',
      startDate: DateUtils.toISOString(startTime),
      endDate: DateUtils.toISOString(endTime),
      receiversEmails: [...attendeeEmails.required, ...attendeeEmails.optional],
      organizerEmail: (organizer?.emailAddress || organizer?.address || '').toLowerCase(),
      requiredAttendees: attendeeEmails.required,
      optionalAttendees: attendeeEmails.optional,
      createdAt: appointmentItem.dateTimeCreated ? DateUtils.toISOString(appointmentItem.dateTimeCreated) : new Date().toISOString(),
      isAllDayEvent: false,
      // Single unique identifier for this appointment
      uniqueId: uniqueAppointmentId
    };
  }

  /**
   * Get a single unique identifier for an appointment
   * Priority order: Item ID > Global Appointment ID > Composite ID
   */
  static getUniqueAppointmentId(appointmentItem) {
    // Priority order for appointment identification:
    // 1. Item ID (Outlook's unique identifier)
    if (appointmentItem.itemId) {
      return appointmentItem.itemId;
    }

    // 2. Global Appointment ID (if available)
    if (appointmentItem.globalAppointmentId) {
      return appointmentItem.globalAppointmentId;
    }

    // 3. Fallback: Create composite identifier
    // Reuse EmailExtractor's composite ID logic (it works for both)
    return this._createCompositeId(appointmentItem);
  }

  /**
   * Create composite ID for appointment
   * Uses the same logic as EmailExtractor for consistency
   */
  static _createCompositeId(appointmentItem) {
    const components = ['appointment'];
    
    // Use subject (first 50 chars)
    if (appointmentItem?.subject) {
      const subject = typeof appointmentItem.subject === 'string' ? appointmentItem.subject : (appointmentItem.subject?.value || '');
      if (subject) {
        components.push(subject.substring(0, 50));
      }
    }
    
    // Use organizer
    if (appointmentItem.organizer?.emailAddress) {
      components.push(appointmentItem.organizer.emailAddress);
    }
    
    // Use start date
    const dateField = appointmentItem.start;
    if (dateField) {
      const dateStr = dateField instanceof Date ? dateField.toISOString() : dateField;
      components.push(dateStr);
    }
    
    // Use first attendee
    if (appointmentItem.requiredAttendees && appointmentItem.requiredAttendees.length > 0) {
      const firstAttendee = appointmentItem.requiredAttendees[0];
      const email = firstAttendee?.emailAddress || firstAttendee?.address || firstAttendee;
      if (email) components.push(email);
    }

    // Create hash-like identifier
    return btoa(components.join('|')).replace(/[+/=]/g, '').substring(0, 32);
  }
}

